import React from 'react';
import { TrendingDown, DollarSign, Clock, Scale } from 'lucide-react';

/**
 * ROI metrics display for a single support generation result.
 * Shows material saved, money saved, and time saved vs standard supports.
 */
export const ROICard = ({ supportResult, customPrices = {} }) => {
  if (!supportResult?.cost_estimate) return null;

  const ce = supportResult.cost_estimate;
  const treeVolume = ce.support_volume_mm3 || 0;
  // Standard block supports typically use 2.8x more material
  const standardVolume = treeVolume * 2.8;
  const savedVolumeMm3 = standardVolume - treeVolume;
  const savedGrams = (savedVolumeMm3 / 1000) * 1.24; // PLA density
  const plaPricePerKg = customPrices.PLA || 20;
  const moneySaved = savedGrams * (plaPricePerKg / 1000);

  const treePrintMinutes = ce.print_time_minutes || 0;
  const standardPrintMinutes = treePrintMinutes * 2.2;
  const timeSaved = standardPrintMinutes - treePrintMinutes;

  const metrics = [
    {
      label: 'Material Saved',
      value: `${savedGrams.toFixed(1)}g`,
      sub: `${((savedVolumeMm3 / (standardVolume || 1)) * 100).toFixed(0)}% less than block supports`,
      icon: Scale,
      color: 'text-green-400',
      bg: 'bg-green-500/10',
      border: 'border-green-500/20',
    },
    {
      label: 'Money Saved',
      value: `$${moneySaved.toFixed(2)}`,
      sub: `Based on $${plaPricePerKg}/kg PLA`,
      icon: DollarSign,
      color: 'text-emerald-400',
      bg: 'bg-emerald-500/10',
      border: 'border-emerald-500/20',
    },
    {
      label: 'Time Saved',
      value: `${timeSaved.toFixed(0)} min`,
      sub: `${treePrintMinutes.toFixed(0)} min vs ~${standardPrintMinutes.toFixed(0)} min standard`,
      icon: Clock,
      color: 'text-sky-400',
      bg: 'bg-sky-500/10',
      border: 'border-sky-500/20',
    },
  ];

  return (
    <div className="space-y-3" data-testid="roi-card">
      <div className="flex items-center gap-2">
        <TrendingDown className="w-5 h-5 text-green-400" />
        <h3 className="text-lg font-semibold text-text-primary">ROI vs Standard Supports</h3>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
        {metrics.map((m) => {
          const Icon = m.icon;
          return (
            <div
              key={m.label}
              className={`p-4 rounded-xl border ${m.bg} ${m.border}`}
              data-testid={`roi-${m.label.toLowerCase().replace(/\s/g, '-')}`}
            >
              <div className="flex items-center gap-2 mb-2">
                <Icon className={`w-4 h-4 ${m.color}`} />
                <span className="text-xs text-text-muted uppercase tracking-wide">{m.label}</span>
              </div>
              <p className={`text-2xl font-bold ${m.color}`}>{m.value}</p>
              <p className="text-xs text-text-secondary mt-1">{m.sub}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};

/**
 * Aggregate ROI summary for the dashboard.
 */
export const ROISummary = ({ data }) => {
  if (!data || data.total_generations === 0) return null;

  const items = [
    { label: 'Material Saved', value: `${data.material_saved_grams}g`, icon: Scale, color: 'text-green-400' },
    { label: 'Money Saved', value: `$${data.money_saved_usd}`, icon: DollarSign, color: 'text-emerald-400' },
    { label: 'Time Saved', value: `${data.time_saved_minutes} min`, icon: Clock, color: 'text-sky-400' },
  ];

  return (
    <div className="bg-bg-light p-5 rounded-xl border border-green-500/20" data-testid="roi-summary">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <TrendingDown className="w-5 h-5 text-green-400" />
          <h3 className="text-lg font-semibold text-text-primary">Your Savings</h3>
        </div>
        <span className="text-xs text-text-muted bg-bg-dark px-2.5 py-1 rounded-full">
          {data.total_generations} generation{data.total_generations !== 1 ? 's' : ''}
        </span>
      </div>
      <div className="grid grid-cols-3 gap-4">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <div key={item.label} className="text-center">
              <Icon className={`w-5 h-5 ${item.color} mx-auto mb-1.5`} />
              <p className={`text-xl font-bold ${item.color}`}>{item.value}</p>
              <p className="text-xs text-text-muted mt-0.5">{item.label}</p>
            </div>
          );
        })}
      </div>
    </div>
  );
};
